import create from 'zustand';
import { Api, subscription, Subscription } from './api';
import { unique } from './arrays';

type ItemFilter = 'all' | 'active' | 'inactive' | 'editing';

type ItemPredicate<T = any> = (v: T) => boolean;
type ItemEquals<T = any> = (a: T, b: T) => boolean;
type ItemSort<T = any> = (a: T, b: T) => number;
type MapList<T = any> = (list: T[], data?: ListData) => T[];

type ListConf<T = any> = {
  active?: ItemPredicate<T>;
  equals?: ItemEquals<T>;
  setActive?: (a: T) => void;
  setInactive?: (a: T) => void;

  allowDuplicates?: boolean;
  id?: (a: T) => string;
  all?: T[];
  editing?: ItemPredicate;
  defaultFilter?: ItemFilter;
  destructive?: boolean;
  preProcess?: (data: ListData) => void;
  postProcess?: MapList;
};

type ListData<T = any> = {
  filter: ItemFilter;
  all: T[];
  values: T[];
  active: T[];
  inactive: T[];
  editing: T; // TODO list support
  added: T;
  removed: T;
  moved: T;
  edited: T;
  status: string;
  error: string;
};

export type MoveItem = {
  currentIndex: number;
  newIndex: number;
};

export type ItemProvider<T = any> = () => T | (() => Promise<T>);

type ListActions<T = any> = {
  setValuesDestructive(a: T[]): void;
  setActiveValuesDestructive(a: T[]): void;
  filter(a: ItemFilter): void;
  add(a: T): number; // set active, update
  remove(a: T, destructive?: boolean): void; // set inactive
  edit(a: T): void; // set active
  replace(a: T, equals?: ItemEquals<T>): void;
  replaceAt(a: T | any, i: number | T): void;
  swap(a: T, b: T): void;
  move(item: MoveItem): void;
  sort(a: ItemSort<T>): void;
  refresh(): void;
  all(): T[];
  values(): T[];
  visit(test: ItemPredicate, fn: (a: T) => void): void;
  cancelEdit(): void;
};

//TODO should form integration be replaced with events ?
//Only actual implementation collection wrapper Apis
type ListForms = {};

type ListEvents<T = any> = Subscription<ListData<T>>;

export type ListApi<T = any> = Api<ListConf<T>, ListData<T>, ListActions<T>, ListForms, ListEvents<T>>;

export function listApi<T = any>(config?: ListConf<T>): ListApi<T> {
  const configuration = {
    ...{
      allowDuplicates: true,
      active: () => true,
      equals: (a: T, b: T) => a === b,
      setActive: () => {},
      setInactive: () => {},
    },
    ...config,
  };

  const { all, active, defaultFilter, setActive, setInactive, allowDuplicates, id, destructive } = configuration;

  const data = create<ListData<T>>(() => ({
    filter: defaultFilter || 'all',
    all: all || [],
    values: [],
    active: [],
    inactive: [],
    editing: null,
    added: null,
    removed: null,
    moved: null,
    edited: null,
    status: null,
    error: null,
  }));

  data.setState({ values: all || [] });
  //TODO: deprecate map, introduce plugins for deduping
  let map: Map<string, T> = new Map();

  if (!allowDuplicates) {
    if (!id) {
      throw Error('Collection with no duplicates requires id function.');
    }
    data.getState().all.forEach((v) => {
      map.set(id(v), v);
    });
  }

  const items: () => T[] = () => data.getState().all;

  function getValues(): T[] {
    return data.getState().values || [];
  }

  function workingValues() {
    return allowDuplicates ? getValues() : items();
  }

  const getFilter = (filter?: ItemFilter) => filter || data.getState().filter || defaultFilter || 'all';

  const postProcess = configuration.postProcess || ((list) => list);
  function filteredValues(a?: T[], filter?: ItemFilter): T[] {
    filter = getFilter(filter);
    let values = (a || []).slice();

    switch (filter) {
      case 'active':
        return values.filter((v) => active(v));
      case 'inactive':
        return values.filter((v) => !active(v));
      case 'all':
      default:
        return values;
    }
  }

  const events = subscription<ListData<T>>(['editing', 'values', 'added', 'removed', 'moved', 'edited'], data);

  function filterValues(f?: ItemFilter): void {
    let all = workingValues();

    // When allowDuplicates=true, workingValues() returns data.getState().values (active items
    // only after a remove). A subsequent add() pushes to values and calls refresh(), which
    // rebuilds 'all' from values alone — dropping inactive items (e.g. deleted CPTs whose
    // DELETE audit must still appear in the audit summary). Re-attach any inactive items that
    // are tracked in state but absent from the current working set.
    if (allowDuplicates && id) {
      const currentInactive = data.getState().inactive || [];
      if (currentInactive.length) {
        const activeIds = new Set(all.map((v) => id(v)));
        const orphanedInactive = currentInactive.filter((v) => !activeIds.has(id(v)));
        if (orphanedInactive.length) {
          all = [...orphanedInactive, ...all];
        }
      }
    }

    const active = filteredValues(all, 'active') || [];
    const inactive = filteredValues(all, 'inactive') || [];
    const editing = filteredValues(all, 'editing') || [];

    if (configuration.preProcess) {
      configuration.preProcess({
        ...data.getState(),
        ...{ all, active, inactive, editing },
      });
    }

    const current = getFilter(f);

    let values: T[];
    switch (current) {
      case 'all':
        values = all;
        break;
      case 'active':
        values = postProcess(active);
        break;
      case 'inactive':
        values = inactive;
        break;
      case 'editing':
        values = editing;
        break;
      default:
        throw new Error('unknown collection filter ' + current);
    }

    // console.log('after setValues',  values,  current, 'active', active, 'all', all)

    data.setState({
      all: all.slice(),
      values: values.slice(),
      active,
      inactive,
      filter: current,
    });
  }

  function setValuesDestructive(all: T[]) {
    all = all || [];

    map = new Map();
    all.forEach((v) => {
      map.set(id(v), v);
    });

    data.setState({ all: all.slice(), values: all.slice() });
    refresh();
  }

  function setActiveValuesDestructive(all: T[]) {
    all = all || [];
    const inactive = data.getState().inactive || [];
    all = all.concat(inactive);

    map = new Map();
    all.forEach((v) => {
      map.set(id(v), v);
    });

    data.setState({ all: all.slice(), values: all.slice() });
    refresh();
  }


  //TODO configure error handling, assumes string errors for now
  function errorString(err: any): string {
    return (err || 'Error').toString();
  }
  const equals: ItemEquals<T> = configuration.equals || ((a, b) => a === b);
  // fire and forget
  function add(a: T) {
    //TODO configure status updates
    data.setState({ error: null, status: null });

    //TODO: make editing merges configurable
    const editing = data.getState().editing;

    try {
      if (!a) {
        throw new Error('No item was provided to add.');
      }
      setActive(a);

      const all = workingValues();

      if (!allowDuplicates) {
        const key = id(a);
        const item = map.get(key);

        if (item) {
          const found = all.filter((v) => id(v) === key);
          if (found.length) {
            const wasActive = active(found[0]);
            const i = all.indexOf(found[0]);
            if (i > -1) {
              a = { ...found[0], ...item, ...a };
              if (wasActive) {
                all[i] = a;
                //    console.log('updating collection ref: ',   all[i])
              } else {
                // The existing entry for this id was soft-removed (inactive but still
                // kept in `all`, e.g. a removed ICD). Re-adding it is a fresh addition,
                // not a revival at its old list position, so move it to the end instead
                // of overwriting it in place - otherwise it reappears where it used to
                // be and bumps every item after it back to its pre-removal position.
                all.splice(i, 1);
                all.push(a);
              }
            }
          } else {
            //TODO make this state impossible:
            //Item is found in the map but not in the active list, replacing items with partial key matches
          }
        } else {
          all.push(a);
        }

        map.set(key, a);
      } else {
        all.push(a);
      }
    } catch (err) {
      data.setState({ error: errorString(err) });
    }

    refresh();

    if (editing && equals(a, editing)) {
      data.setState({ edited: editing, editing: null });
    } else {
      data.setState({ added: a });
    }
    return all ? all.indexOf(a) : -1;
  }
  function cancelEdit() {
    data.setState({ edited: null, editing: null });
  }
  function remove(a: T, destructiveOverride?: boolean) {
    const all = workingValues();
    data.setState({ error: null, status: null });

    const currentDestructive = destructive || destructiveOverride;

    let toRemove: T;
    try {
      if (!a) {
        throw new Error('No item was provided to remove.');
      }
      toRemove = all.filter((v) => equals(v, a))[0];

      if (toRemove) {
        setInactive(toRemove);
        // console.log('remvoved set inactive', a,  toRemove);
      } else {
        throw new Error('Attempting to remove item that does not exist in collection.');
      }
    } catch (err) {
      data.setState({ error: errorString(err) });
    }

    if (toRemove) {
      //    console.log('removed', toRemove, 'currentDestructive', currentDestructive)
      data.setState({ removed: toRemove });

      if (currentDestructive) {
        all.splice(all.indexOf(toRemove), 1);
        // TODO: test this change in all workflows
        // setValuesDestructive(data.getState().values);
        setValuesDestructive(all);
      }
    }

    refresh();
  }
  function move(m: MoveItem): void {
    const val = getValues();
    const item = val.splice(m.currentIndex, 1)[0];

    if (item) {
      val.splice(m.newIndex, 0, item);
      //need to update the positions of the same items in the all collection
      let all = data.getState().all;
      const diff = unique(all, val, equals);
      // console.log('all', all, 'values', val, 'unique', diff);
      all = val.concat(diff);

      data.setState({ values: val.slice(), all, moved: item });
    }
  }

  function edit(editing: T): void {
    data.setState({ editing });
  }

  function sort(a: ItemSort<T>): void {
    const current = getValues();
    const values = current.sort(a);
    data.setState({ values });
  }

  const filter = (f: ItemFilter) => {
    data.setState({ filter: f });
    refresh();
  };

  const refresh = () => filterValues(null);

  function visit(test: ItemPredicate, fn: (a: T) => void): void {
    let visited = false;
    workingValues().forEach((v) => {
      if (test(v)) {
        fn(v);
        visited = true;
      }
    });
    if (visited) {
      refresh();
    }
  }

  function isObject(v: any) {
    return typeof v === 'object';
  }
  function replace(a: T | any, equalsFn?: ItemEquals<T>): void {
    equalsFn = equalsFn || equals;

    const all = workingValues();
    const found = all.filter((v) => equalsFn(v, a))[0];

    console.info('replace, found', a, found);

    if (!!found) {
      const i = all.indexOf(found);
      if (i > -1) {
        if (isObject(a)) {
          all[i] = { ...found[0], ...a };
        } else {
          all[i] = a;
        }

        refresh();
      }
    } else {
      console.info('No replace item found for', id(a));
    }
  }

  function replaceAt(a: T | any, target: number | T): void {
    const all = workingValues();

    let i = NaN;
    if (isNaN(target as any)) {
      const eid = id(target as T);
      const found = all.find((v) => id(v) === eid);
      i = all.indexOf(found as T);
    } else {
      i = target as number;
    }

    if (isNaN(i)) {
      return;
    }
    const found = all[i];
    if (!!found) {
      map.delete(id(found));

      if (isObject(a)) {
        all[i] = { ...found[0], ...a };
      } else {
        all[i] = a;
      }

      map.set(id(all[i]), all[i]);

      refresh();
    } else {
      console.info('No replace item found for', a);
    }
  }

  function swap(a: T, b: T): void {
    const all = workingValues();
    const i = all.indexOf(a);
    if (i > -1) {
      all[i] = b;
      refresh();
    } else {
      console.info('no swap match found for', a);
    }
  }

  const actions: ListActions<T> = {
    setValuesDestructive,
    setActiveValuesDestructive,
    add,
    remove,
    replace,
    replaceAt,
    swap,
    move,
    edit,
    filter,
    sort,
    refresh,
    visit,
    cancelEdit,
    all: items, // TODO: deprecate
    values: getValues,
  };

  const forms = {};

  filterValues();

  // // console.log(data.getState());

  return {
    configuration,
    data,
    actions,
    forms,
    events,
  };
}

export function inactiveListApi<T = any>(existing: ListApi<T>): ListApi<T> {
  function filterValues(): T[] {
    return (existing.data.getState().inactive || []) as T[];
  }

  const data = create<ListData<T>>(() => ({
    filter: 'all',
    values: filterValues(),
    all: filterValues().slice(),
    active: [],
    inactive: [],
    editing: null,
    added: null,
    removed: null,
    moved: null,
    edited: null,
    status: null,
    error: null,
  }));

  existing.data.subscribe(
    () => {
      //  console.log('inactive  change', filterValues());
      data.setState({ values: filterValues() });
    },
    (s) => s.values
  );

  const events = subscription<ListData<T>>(['editing', 'values', 'added', 'removed', 'moved', 'edited'], data);

  //  console.log('init values', data.getState().values),
  const refresh = () => {
    data.setState({ values: filterValues().slice() });
  };
  // TODO: extract partial API for use in table components
  const actions: ListActions = {
    values: () => filterValues(),
    refresh,
    add: null,
    remove: null,
    replace: null,
    replaceAt: null,
    swap: null,
    edit: null,
    move: null,
    visit: null,
    filter: null,
    setValuesDestructive: null,
    setActiveValuesDestructive: null,
    cancelEdit: null,
    sort: null,
    all: null,
  };

  return {
    data,
    events,
    actions,
    configuration: existing.configuration,
  };
}
